import type {
  Appeal,
  AppealStatus,
  ItemModeration,
  ModerationInputKind,
  ModerationRefusal,
} from "@pax/api-contract/moderation/v1alpha1";
import { APPEAL_COPY, INPUT_NAMES, INPUT_WORDS, type InputWords } from "@pax/moderation-copy";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { orpc } from "@/lib/api";
import { ALERT, AppealProgress, QUIET } from "./AppealProgress";
import { smallWarning } from "./refusals";
import { AppealLine } from "./SubmitError";

const OPEN: readonly AppealStatus[] = ["awaiting_ai", "awaiting_escalation", "awaiting_human"];

/** What an open appeal, or one a moderator rejected while its refusal stands, says. */

/**
 * An item's refusal and appeal, for its owner, on the item itself: the refusal with a way to appeal
 * it, the appeal's progress with a way to escalate or withdraw it, or how it was decided.
 */
export function ItemAppealNotice({
  moderation,
  words,
  overturned,
  onChange,
  canAppeal = true,
}: {
  moderation: ItemModeration | null | undefined;
  /** Whether you may appeal its refusal, where not everyone may, as on a preset's team. */
  canAppeal?: boolean;
  /** What was refused, when its input kind alone doesn't say, as for a preset judged whole. */
  words?: InputWords;
  /** What to say once an appeal succeeds, for an item that doesn't go up by itself when it does. */
  overturned?: string;
  /** After the user appeals, escalates, or withdraws, to read the item again. */
  onChange: () => unknown;
}) {
  const appealId = moderation?.appeal?.id;
  const appeal = useQuery({
    ...orpc.moderation.appeals.get.queryOptions({
      input: { params: { appealId: appealId ?? "" } },
    }),
    enabled: appealId !== undefined,
  });
  if (!moderation || (appealId && !appeal.data)) return null;
  return (
    <AppealOrRefusal
      appeal={appeal.data?.appeal ?? null}
      refusal={moderation.refusal}
      words={words}
      overturned={overturned}
      onChange={onChange}
      canAppeal={canAppeal}
    />
  );
}

/**
 * The open appeals on the user's own profile, one line per field among `inputKinds`, from the
 * moderation state Get a profile returns to its owner. A refused change is offered for appeal only
 * in the error its save returned.
 */
export function ProfileAppeals({
  profileId,
  inputKinds,
}: {
  profileId: string;
  inputKinds: readonly ModerationInputKind[];
}) {
  const queryClient = useQueryClient();
  const profile = orpc.profiles.get.queryOptions({ input: { params: { profileId } } });
  const { data: moderation } = useQuery({ ...profile, select: response => response.moderation });
  if (!moderation) return null;
  const refresh = () => queryClient.invalidateQueries({ queryKey: profile.queryKey });
  const appeals = moderation.appeals.flatMap(appeal =>
    appeal.kind === "refusal" && inputKinds.includes(appeal.refusal.inputKind) ? [appeal] : [],
  );
  if (appeals.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      {appeals.map(appeal => (
        <AppealProgress
          key={appeal.id}
          label={INPUT_NAMES[appeal.refusal.inputKind]}
          appeal={appeal}
          onChange={refresh}
        />
      ))}
    </div>
  );
}

/** One input's latest appeal and standing refusal, as the one line that matters now. */
function AppealOrRefusal({
  appeal,
  refusal,
  words,
  overturned,
  onChange,
  canAppeal,
}: {
  appeal: Appeal | null;
  refusal: ModerationRefusal | null;
  words?: InputWords;
  overturned?: string;
  onChange: () => unknown;
  canAppeal: boolean;
}) {
  const progress = (shown: Appeal) => <AppealProgress appeal={shown} onChange={onChange} />;
  const aboutRefusal =
    appeal?.kind === "refusal" &&
    refusal !== null &&
    appeal.refusal.classificationId === refusal.classificationId;
  if (appeal && OPEN.includes(appeal.status)) {
    return progress(appeal);
  }
  if (refusal) {
    if (aboutRefusal && appeal.status === "upheld") return progress(appeal);
    return (
      <div role="status" className={ALERT}>
        {aboutRefusal && appeal.status === "withdrawn" && <p>{APPEAL_COPY.withdrawn}</p>}
        <p>{smallWarning(refusal)}</p>
        <AppealLine
          refusal={refusal}
          words={words ?? INPUT_WORDS[refusal.inputKind]}
          onAppealed={onChange}
          allowed={canAppeal}
        />
      </div>
    );
  }
  if (overturned && appeal?.status === "overturned") {
    return (
      <p role="status" className={QUIET}>
        {overturned}
      </p>
    );
  }
  return null;
}
